import { useCallback, useEffect, useRef, useState } from 'react';

interface UseScrollRevealOptions {
  threshold?: number;
  rootMargin?: string;
  key?: string | number;
}

const useScrollReveal = <T extends HTMLElement>(itemCount: number, options: UseScrollRevealOptions = {}) => {
  const { threshold = 0.3, rootMargin = '-50px 0px', key } = options;

  const itemRefs = useRef<(T | null)[]>([]);
  const [visibleItems, setVisibleItems] = useState<Set<number>>(new Set());

  useEffect(() => {
    setVisibleItems(new Set());
  }, [key]);

  const setRef = useCallback(
    (index: number) => (el: T | null) => {
      itemRefs.current[index] = el;
    },
    []
  );

  const isVisible = useCallback((index: number) => visibleItems.has(index), [visibleItems]);

  const reset = useCallback(() => {
    setVisibleItems(new Set());
  }, []);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    itemRefs.current.forEach((ref, index) => {
      if (!ref) return;

      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              setVisibleItems(prev => new Set([...prev, index]));
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold, rootMargin }
      );

      observer.observe(ref);
      observers.push(observer);
    });

    return () => {
      observers.forEach(observer => observer.disconnect());
    };
  }, [itemCount, threshold, rootMargin, key]);

  return { setRef, isVisible, visibleItems, reset };
};

export default useScrollReveal;
